import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEraser } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsersService } from 'src/app/services/http/users.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { TChatStateService } from 'src/app/services/state/tchat-state.service';
import { FormFocusDirective } from '../../../core/directives/form-focus.directive';
import { ITchatUser } from '../../../models/server/interface/tchat-user.interface';
import { TchatUser } from '../../../models/server/tchat-user';
import { TchatUserIdentityComponent } from '../tchat-user-identity/tchat-user-identity.component';

@Component({
    selector: 'app-tchat-users',
    templateUrl: './tchat-users.component.html',
    styleUrls: ['./tchat-users.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatIconModule,
        NgClass,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        TchatUserIdentityComponent,
    ]
})
export class TchatUsersComponent implements OnInit, OnDestroy {
    public resetFilterIcon = faEraser;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Emis lorsqu'un utilisateur est sélectionné.
     */
    @Output()
    selectionChanged: EventEmitter<ITchatUser> = new EventEmitter<ITchatUser>();

    /**
     * Liste de tous les utilisateurs de l'organisation.
     */
    public rawUsers: ITchatUser[];

    /**
     * Liste de tous les utilisateurs de l'organisation respectant les filtres.
     */
    public filteredUsers: ITchatUser[];

    /**
     * Utilisateur selectionné.
     */
    public selectedUser: ITchatUser = null;

    /**
     * Formulaire contenant les filtres de recherche.
     */
    filtersForm: UntypedFormGroup;

    /**
     * Constructeur.
     * @param tchatService
     * @param usersService
     * @param connectedUserStateService
     * @param cdRef
     */
    constructor(
        private tchatService: TChatStateService,
        private usersService: UsersService,
        private connectedUserStateService: ConnectedUsersStateService,
        private cdRef: ChangeDetectorRef
    ) {
        if (this.tchatService.getUsers$) {
            this.usersService
                .List(null, null, null)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((users) => {
                    this.tchatService.setUsers$(users.value.map((user) => new TchatUser(user.account, `${user.displayName}`, false, null)));
                });
        }
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.filtersForm = new UntypedFormGroup({});
        this.filtersForm.addControl('searchControlName', new UntypedFormControl());

        this.subscribeToUserStateChangeEvents();

        this.subscribeToNotificationEvents();
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelée lorsque l'utilisateur selectionne un utilisateur.
     * @param userSelected Utilisateur sélectionné.
     */
    public onSelected(userSelected: ITchatUser) {
        this.selectedUser = userSelected;
        this.selectionChanged.emit(userSelected);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Rechercher'
     */
    public onSearch(): void {
        let currentFilterValue = this.filtersForm.controls['searchControlName'].value;

        if (!!currentFilterValue) {
            const datas = this.rawUsers.slice();
            this.filteredUsers = datas.filter((x) => x.displayName.toLowerCase().includes(currentFilterValue.toLowerCase()));
        } else {
            this.resetFilter();
        }

        this.cdRef.detectChanges();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône de réinitialisation du filtre.
     */
    public resetFilter(): void {
        this.filteredUsers = this.rawUsers.slice();
        this.filtersForm.controls['searchControlName'].setValue(null);
    }

    /**
     * S'abonne aux événements de notification.
     */
    private subscribeToNotificationEvents() {
        this.tchatService.notifications$.pipe(takeUntil(this.componentDestroyed$)).subscribe((notifications) => {
            this.rawUsers.forEach((u) => {
                let n = notifications.find((n) => n.users.map((x) => x.login).includes(u.login));
                u.notifications = !!n ? n.notification : 0;
            });
            this.filteredUsers = this.rawUsers.slice();
            this.cdRef.detectChanges();
        });
    }

    /**
     * S'abonne aux événements de changement d'état des utilisateurs.
     */
    private subscribeToUserStateChangeEvents() {
        this.tchatService.getUsers$.pipe(takeUntil(this.componentDestroyed$)).subscribe((users) => {
            if (!!users) {
                this.rawUsers = users
                    .sort((a, b) => {
                        if (a.displayName.toLowerCase() > b.displayName.toLowerCase()) {
                            return 1;
                        } else {
                            return -1;
                        }
                    })
                    .filter((user) => user.login != this.connectedUserStateService.getUserConnected().login);
                this.filteredUsers = this.rawUsers.slice();
                this.cdRef.detectChanges();
            }
        });
    }
}
